import { useTranslation } from 'react-i18next'

export type ToastVariant = 'success' | 'error' | 'info'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastItem {
  id: string
  message: string
  variant: ToastVariant
  /** Optional follow-up action; activating it also dismisses the toast. */
  action?: ToastAction
}

interface ToastProps {
  toast: ToastItem
  onDismiss: (id: string) => void
}

// Status bands intentionally stay branded (success/error/warning are
// status signals, not chrome) but pick up `dark:` variants so the
// container reads against dark canvases. Info uses tokens.
//
// FILLS ARE OPAQUE. The dark variants used to carry `/60`, which let the
// canvas behind show through — board cards and columns sat visibly
// inside the toast and cost the message its legibility. A toast is a
// transient overlay that has to be readable on first glance over
// whatever happens to be underneath it, and none of that varies, so
// there is nothing for translucency to buy. The light variants were
// always opaque and `info` resolves to an opaque token, so this also
// makes the three agree. The BORDER keeps its alpha deliberately: it
// blends against a known fill rather than the page.
const variantClasses: Record<ToastVariant, string> = {
  success:
    'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950 dark:text-emerald-100',
  error:
    'border-red-200 bg-red-50 text-red-900 dark:border-red-500/30 dark:bg-red-950 dark:text-red-100',
  info: 'border-border bg-bg-elevated text-fg',
}

export function Toast({ toast, onDismiss }: ToastProps) {
  const { t } = useTranslation('common')
  return (
    <output
      aria-live="polite"
      className={`pointer-events-auto w-72 rounded-md border shadow-md transition-opacity ${variantClasses[toast.variant]}`}
    >
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={t('toast.dismissAriaLabel')}
        className="block w-full px-4 py-3 text-start text-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent"
      >
        {toast.message}
      </button>
      {/* A sibling of the dismiss button, never nested inside it — a button
          within a button is invalid HTML and an axe nested-interactive
          violation. */}
      {toast.action ? (
        <div className="flex justify-end px-4 pb-3">
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick()
              onDismiss(toast.id)
            }}
            className="rounded-sm text-sm font-semibold underline underline-offset-2 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {toast.action.label}
          </button>
        </div>
      ) : null}
    </output>
  )
}
